import { useCallback, useEffect, useState } from "react";
import { api, ApiError } from "../lib/api.js";
import type { PromiseView, PromisesResult } from "../lib/types.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { useModelPicker } from "../ai/ModelPicker.js";

interface Props {
  projectId: string;
  onOpenScene: (sceneId: string) => void;
  onChanged?: () => void;
}

const TYPE_LABEL: Record<string, string> = {
  setup: "Setup", promise: "Promise", foreshadowing: "Foreshadow", question: "Question", commitment: "Commitment"
};
const HEALTH: Record<string, { label: string; cls: string }> = {
  open: { label: "Open", cls: "" },
  paid: { label: "Paid off", cls: "insight-ok" },
  forgotten: { label: "Possibly forgotten", cls: "insight-warn" },
  dismissed: { label: "Dismissed", cls: "" }
};

/**
 * Story Promises — the setup/payoff tracker. Shows what the book has promised the
 * reader and whether it paid off, with chronology-based "possibly forgotten"
 * reasons. Conservative AI extraction; manual control; deep-links to scenes.
 */
export function PromisesPanel({ projectId, onOpenScene, onChanged }: Props) {
  const toast = useToast();
  const { pickModel } = useModelPicker();
  const [data, setData] = useState<PromisesResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [extracting, setExtracting] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await api.promises(projectId)); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't load promises"); }
    finally { setLoading(false); }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load]);

  const extract = async () => {
    const choice = await pickModel({ task: "extraction" });
    if (!choice) return;
    setExtracting(true);
    try {
      const res = await api.extractPromises(projectId, choice);
      toast.success(`Found ${res.result.created} promise${res.result.created === 1 ? "" : "s"}, linked ${res.result.paidLinked} payoff${res.result.paidLinked === 1 ? "" : "s"}${res.result.reviewed ? `, ${res.result.reviewed} to review` : ""}.`);
      setData(res);
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : err instanceof Error ? err.message : "Extraction failed");
    } finally { setExtracting(false); }
  };

  const addManual = async () => {
    const title = newTitle.trim();
    if (!title) return;
    try { await api.createPromise(projectId, { title }); setNewTitle(""); await load(); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't add"); }
  };

  const setStatus = async (p: PromiseView, status: "open" | "paid" | "dismissed") => {
    try { await api.updatePromise(projectId, p.id, { status }); await load(); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Update failed"); }
  };
  const del = async (p: PromiseView) => {
    try { await api.deletePromise(projectId, p.id); await load(); onChanged?.(); }
    catch (err) { toast.error(err instanceof Error ? err.message : "Delete failed"); }
  };

  if (loading) return <div className="insights-loading"><Icon name="sparkles" size={22} className="spin" style={{ color: "var(--accent)" }} /></div>;
  if (!data) return null;

  const order = ["forgotten", "open", "paid", "dismissed"];
  const sorted = [...data.promises].sort((a, b) => order.indexOf(a.health) - order.indexOf(b.health));

  return (
    <div className="intel-scroll">
      <section className="card insight-card">
        <div className="insight-card-head">
          <Icon name="target" size={16} style={{ color: "var(--accent)" }} />
          <h3>Story Promises</h3>
          <span className="spacer" />
          <button className="btn btn-sm btn-ghost" onClick={() => void extract()} disabled={extracting}>
            <Icon name={extracting ? "clock" : "sparkles"} size={13} className={extracting ? "spin" : ""} /> {extracting ? "Finding…" : "Find promises (AI)"}
          </button>
        </div>
        <div className="polish-metrics" style={{ marginBottom: 12 }}>
          <div className="polish-metric"><div className="polish-metric-value">{data.counts.open}</div><div className="polish-metric-label">Open</div></div>
          <div className="polish-metric"><div className="polish-metric-value">{data.counts.paid}</div><div className="polish-metric-label">Paid off</div></div>
          <div className="polish-metric"><div className="polish-metric-value">{data.counts.forgotten}</div><div className="polish-metric-label">Possibly forgotten</div></div>
        </div>
        <div className="revmode-add">
          <input className="input" placeholder="Add a promise/setup to track…" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void addManual(); }} />
          <button className="btn btn-sm btn-primary" onClick={() => void addManual()} disabled={!newTitle.trim()}>Add</button>
        </div>

        {sorted.length === 0 ? (
          <p className="muted insight-empty">No promises tracked yet. Add one, or "Find promises" to detect setups and payoffs across the book.</p>
        ) : (
          <div className="intel-entities">
            {sorted.map((p) => {
              const h = HEALTH[p.health] ?? { label: p.health, cls: "" };
              return (
                <div key={p.id} className="intel-entity">
                  <div className="intel-entity-head">
                    <span className="chip intel-type">{TYPE_LABEL[p.type] ?? p.type}</span>
                    <span className="strong">{p.title}</span>
                    <span className="spacer" />
                    <span className={`chip ${h.cls}`}>{h.label}</span>
                  </div>
                  <div className="intel-entity-meta faint">
                    {p.setupLabel && <button className="insight-link" onClick={() => p.setupSceneId && onOpenScene(p.setupSceneId)}>setup: {p.setupLabel}</button>}
                    {p.payoffLabel && <button className="insight-link" onClick={() => p.payoffSceneId && onOpenScene(p.payoffSceneId)}>payoff: {p.payoffLabel}</button>}
                  </div>
                  {p.detail && <p className="intel-desc">{p.detail}</p>}
                  {p.healthReason && <p className="intel-reason"><Icon name="alert" size={11} /> {p.healthReason}</p>}
                  <div className="review-actions" style={{ marginTop: 8 }}>
                    {p.health !== "paid" && <button className="btn btn-sm btn-ghost" onClick={() => void setStatus(p, "paid")}><Icon name="check" size={12} /> Mark paid</button>}
                    {p.status !== "open" && <button className="btn btn-sm btn-ghost" onClick={() => void setStatus(p, "open")}><Icon name="undo" size={12} /> Reopen</button>}
                    {p.status !== "dismissed" && <button className="btn btn-sm btn-ghost" onClick={() => void setStatus(p, "dismissed")}>Dismiss</button>}
                    <button className="btn btn-icon btn-ghost" title="Delete" onClick={() => void del(p)}><Icon name="trash" size={13} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
